import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { useAuth, useRole } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { canViewCost } from '@/types/schemas';
import { fetchCategories } from './setupQueries';
import { fetchSuppliers } from './supplierQueries';
import {
  buildImportReviewRows,
  downloadImportTemplate,
  importProductRows,
  parseExcelFile,
  type ImportReviewRow,
} from './lib/import-products';

export function ImportProductsDialog() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const role = useRole();
  const showCost = canViewCost(role);
  const storeId = useCurrentStore((s) => s.currentStoreId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ImportReviewRow[]>([]);
  const [importing, setImporting] = useState(false);

  async function handleFile(f: File) {
    if (!storeId) {
      toast({ title: 'Select a store first', variant: 'destructive' });
      return;
    }
    try {
      const buffer = await f.arrayBuffer();
      const parsed = await parseExcelFile(buffer);
      const categories = await fetchCategories();
      const suppliers = await fetchSuppliers();
      const review = buildImportReviewRows(parsed, categories, suppliers, { canViewCost: showCost });
      setRows(review);
      setOpen(true);
    } catch (e) {
      toast({ title: 'Could not read file', description: String(e), variant: 'destructive' });
    }
  }

  async function runImport() {
    if (!user || !storeId) return;
    setImporting(true);
    try {
      const categories = await fetchCategories();
      const suppliers = await fetchSuppliers();
      const validRows = rows.filter((r) => r.status !== 'error');
      const result = await importProductRows(validRows, {
        storeId,
        userId: user.uid,
        canViewCost: showCost,
        categories: [...categories],
        suppliers: [...suppliers],
      });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      qc.invalidateQueries({ queryKey: ['inventory-categories'] });
      qc.invalidateQueries({ queryKey: ['inventory-brands'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast({
        title: 'Import complete',
        description: `${result.imported} imported, ${result.failed} failed`,
        variant: 'success',
      });
      setOpen(false);
      setRows([]);
    } catch (e) {
      toast({ title: 'Import failed', description: String(e), variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Upload className="h-4 w-4" />
            <span className="hidden sm:inline">Import</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => inputRef.current?.click()}>
            <Upload className="h-4 w-4" />
            Upload Excel / CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => downloadImportTemplate()}>
            <Download className="h-4 w-4" />
            Download template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import preview</DialogTitle>
            <DialogDescription>
              {rows.length} rows · valid/warning rows will be imported to current store stock.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto text-sm">
            {rows.map((r) => (
              <div
                key={r.rowIndex}
                className={`rounded border p-2 ${r.status === 'error' ? 'border-destructive/50 bg-destructive/5' : r.status === 'warning' ? 'border-amber-300 bg-amber-50/50' : ''}`}
              >
                <div className="font-medium">Row {r.rowIndex}: {r.brand} — {r.category}</div>
                <ul className="text-xs text-muted-foreground mt-1">
                  {r.messages.map((m: string, i: number) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="gold" disabled={importing} onClick={() => runImport()}>
              {importing ? 'Importing…' : 'Import valid rows'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
